import { routes } from '@/core/routing/routes/routes.gb';
import { createURL } from '@/core/routing/URLParser';
import { DeliveryLabelType } from '@/core/tamagoshi/ProductCard/components/DeliveryLabel/DeliveryLabel';
import type { Merchandising } from '@/core/tamagoshi/ProductCard/interfaces';
import { type ProductCardFragment } from '@/domains/productDiscovery/Recommendation/services/graphql/generated/productCard.fragment.generated';

/**
 * Retrieves the appropriate delivery label based on the provided cold delivery offers.
 *
 * @param coldDelivery - The cold delivery offers associated with a product.
 * @returns The delivery label type, which can be one of the following:
 * - 'freeDeliveryByMM': If the delivery is managed by MM.
 * - 'freeDelivery': If the cheapest offer has a delivery price of zero.
 * - 'fastDelivery': If there is an express delivery offer.
 * - `undefined`: If no cold delivery offers are provided.
 */
export const getDeliveryLabels = (
  coldDelivery?: ProductCardFragment['coldDeliveryOffers'],
): DeliveryLabelType | undefined => {
  if (!coldDelivery) return undefined;

  const { isMf, cheapestOffer, hasExpressOffer } = coldDelivery;
  const hasFreeDelivery = cheapestOffer?.deliveryPrice.amount === 0;
  if (isMf) {
    return hasFreeDelivery ? 'freeManoExpressDelivery' : 'manoExpressDelivery';
  }

  if (hasFreeDelivery) return 'freeDelivery';

  if (hasExpressOffer) return 'fastDelivery';
};

/**
 * Creates a URL for a product using the provided product details.
 *
 * @param {ProductCardFragment} product - The product details used to generate the URL.
 * @returns {string} The generated product URL.
 */
export const createProductUrl = (product: ProductCardFragment) => {
  return createURL(
    routes.product,
    {
      slug: product.sku.masterProduct.slug,
      id: product.sku.masterProduct.articleId,
    },
    { product_id: product.productId },
  );
};

/**
 * Extracts merchandising labels from the provided commercial animation tags.
 *
 * @param commercialLabels - The commercial animation object from which to extract tags.
 * @returns An array of merchandising labels. If no commercial labels are provided, returns an empty array.
 */
export const getMerchLabels = (
  commercialLabels?: ProductCardFragment['commercialAnimation'],
): Merchandising[] => {
  if (!commercialLabels) return [];

  return commercialLabels.tags as Merchandising[];
};
